/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useCatalogItemHelper } from '../../../hooks/useCatalogItemHelper';
import { QuickSetupComponentProps } from '../../../util/types';
import { AuthenticationContent } from './AuthenticationContent';
import { AuthenticationStepMessages as messages } from './messages';

/**
 * AuthenticationStep Component
 *
 * This component represents a step in a quick setup process (and not used for edit process).
 * This step determines if the next button should be enabled or not based on fields validity based on selected auth type.
 */
export const AuthenticationStep = ({
  assistantId,
  catalogItemId,
  environmentId,
  markStepComplete,
  readOnly
}: QuickSetupComponentProps) => {
  const formMethods = useFormContext();

  const { getCatalogFieldsValid } = useCatalogItemHelper(
    formMethods,
    catalogItemId
  );

  // When field is valid, mark step complete in quick setup and enable next button
  useEffect(() => {
    const fieldsValid = getCatalogFieldsValid();
    markStepComplete(fieldsValid);
  }, [getCatalogFieldsValid, markStepComplete]);

  return (
    <div className="TabContent__body-content TabContent__body-outdent">
      <div className="QuickSetup__step-title">
        <FormattedMessage {...messages.title} />
      </div>
      <div className="TabContent__spacer8" />
      <AuthenticationContent
        assistantId={assistantId}
        catalogItemId={catalogItemId}
        environmentId={environmentId}
        readOnly={readOnly}
        isQuickSetup
      />
    </div>
  );
};
